'use client';

import { useEffect, useState } from 'react';
import { Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import ResponsiveImage, { type ResponsiveImageData } from '@/components/atoms/ResponsiveImage';
import type { PagePart_UspGrid_Entry } from '@/types/graphql';
import { cn } from '@/utils/cn';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';

interface UspGridProps {
    pagePart: PagePart_UspGrid_Entry;
}

export default function UspGrid({ pagePart }: UspGridProps) {
    const images = (pagePart.uspGridImages || []).filter(Boolean) as ResponsiveImageData[];
    const [isLargeScreen, setIsLargeScreen] = useState(false);

    useEffect(() => {
        const checkScreenSize = () => {
            setIsLargeScreen(window.innerWidth >= 1024); // lg breakpoint
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Determine if we should show swiper or grid for images only
    const shouldShowSwiper = (itemCount: number) => {
        if (!isLargeScreen) return true; // Always swiper on smaller screens
        return itemCount > 3; // Only swiper on lg+ if more than 3 items
    };

    // Grid classes for when not using swiper (images) and for USP items
    const getGridClasses = (itemCount: number) => {
        if (itemCount === 1) {
            return 'grid grid-cols-1 gap-4 gap-y-8lg:gap-8';
        }
        if (itemCount === 2) {
            return 'grid grid-cols-1 gap-4 gap-y-8 md:grid-cols-2 lg:gap-8';
        }
        // 3 or more items
        return 'grid grid-cols-1 gap-4 gap-y-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-8';
    };

    const renderImages = () => {
        if (images.length === 0) return null;

        if (shouldShowSwiper(images.length)) {
            return (
                <div className="mb-8">
                    <Swiper
                        spaceBetween={16}
                        slidesPerView={1}
                        navigation={true}
                        keyboard={{
                            enabled: true,
                            onlyInViewport: true,
                            pageUpDown: true,
                        }}
                        breakpoints={{
                            1024: {
                                slidesPerView: 3,
                                spaceBetween: 24,
                            },
                        }}
                        modules={[Navigation, Keyboard]}
                        className="pb-12 text-sm leading-6"
                        tabIndex={0}
                        aria-roledescription="carousel"
                    >
                        {images.map((image, index) => (
                            <SwiperSlide
                                key={image.id ?? index}
                                role="tabpanel"
                                aria-roledescription="slide"
                                aria-label={`Slide ${index + 1} of ${images.length}`}
                            >
                                <div className="h-full">
                                    <ResponsiveImage
                                        image={image}
                                        sizes="(max-width: 1024px) 100vw, 33vw"
                                        className="h-full w-full object-cover"
                                    />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            );
        }

        // Grid layout for lg+ with 3 or fewer items
        return (
            <div className={cn('mb-8', getGridClasses(images.length))}>
                {images.map((image, index) => (
                    <div key={image.id ?? index}>
                        <ResponsiveImage
                            image={image}
                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                            className="h-full w-full object-cover"
                        />
                    </div>
                ))}
            </div>
        );
    };

    const renderUspItems = () => {
        if (pagePart.uspGridItems.length === 0) return null;

        // USP items always use grid layout
        return (
            <div className={cn(getGridClasses(pagePart.uspGridItems.length))}>
                {pagePart.uspGridItems.map((item) =>
                    item ? (
                        <div key={item.title} className="flex flex-row items-start gap-2">
                            <CheckBadgeIcon className="mt-1 block h-6 w-6 flex-shrink-0 text-haug-choco-100" />
                            <div>
                                <div className="font-bold text-haug-choco-100 text-lg uppercase md:text-xl lg:text-2xl">
                                    {item.title}
                                </div>
                                <div className="text-haug-choco-100">{item.uspSubtext}</div>
                            </div>
                        </div>
                    ) : null
                )}
            </div>
        );
    };

    return (
        <div className="page-part-padding bg-haug-ivory-100 font-neue-haas">
            <div className="py-8 md:py-12">
                <div className="mb-8">
                    <h2 className="font-bold text-2xl text-haug-choco-100 md:text-3xl lg:text-4xl">
                        {pagePart.title}
                    </h2>
                    {pagePart.uspGridSubtext && (
                        <div className="mt-4 text-base text-haug-choco-100">
                            {pagePart.uspGridSubtext}
                        </div>
                    )}
                </div>
                {renderImages()}
                {renderUspItems()}
            </div>
        </div>
    );
}
